import { defineStore } from "pinia";
import { markRaw } from "vue";

import {
  POINT_PIVOT_LEFT_DRAGS,
  type TPivotLeftDrag,
} from "@/lib/camera/pointPivot.ts";
import {
  levelAxesAreIdentical,
  type TStreamlineLevelInfo,
  type TVectorVariablePair,
  type TVectorVariableSelection,
} from "@/lib/data/vectorField.ts";
import type { TVectorMagnitudeInfo } from "@/lib/data/vectorMagnitude.ts";
import type { TDigestProbe } from "@/lib/digest/digestProbe.ts";
import {
  DEFAULT_PERCENTILE,
  DEFAULT_PERCENTILE_HIGH,
  DEFAULT_PERCENTILE_LOW,
} from "@/lib/digest/digestVariables.ts";
import {
  DEFAULT_STRATUM,
  isStratumChoice,
  type TStratumChoice,
} from "@/lib/digest/strata.ts";
import { DEFAULT_GAP_FILL_SIZE, GAP_FILL_SIZES } from "@/lib/grids/gapFill.ts";
import {
  LAND_SEA_MASK_MODES,
  type TLandSeaMaskMode,
} from "@/lib/layers/landSeaMask.ts";
import {
  NOISE_POINT_OPACITY,
  POINT_OPACITY_RANGE,
} from "@/lib/points/pointBudget.ts";
import { POINT_COLOURS, type TPointColour } from "@/lib/points/pointTimes.ts";
import type { TDistanceScale } from "@/lib/projection/distanceScale.ts";
import {
  PROJECTION_TYPES,
  type TProjectionCenter,
  type TProjectionType,
} from "@/lib/projection/projectionUtils.ts";
import type { TViewFootprint } from "@/lib/projection/viewFootprint.ts";
import type { TColorMap } from "@/lib/shaders/colormapShaders.ts";
import type { TVarInfo, TBounds } from "@/lib/types/GlobeTypes.ts";
import type { TCatalog } from "@/utils/catalog.ts";
import type { THistogramSummary } from "@/utils/histogram.ts";

export const HOVERED_GRID_POINT_STATUS = {
  VALUE: "value",
  MISSING: "missing",
} as const;

export type THoveredGridPointStatus =
  (typeof HOVERED_GRID_POINT_STATUS)[keyof typeof HOVERED_GRID_POINT_STATUS];

export type THoveredGridPoint = {
  lat: number;
  lon: number;
  value: number | null;
  status: THoveredGridPointStatus;
  // the value was filled from neighbouring cells, not observed
  interpolated?: boolean;
  // the level the value is read from, when not the one on screen
  level?: string;
  screenX: number;
  screenY: number;
};

/** What the point tier has on screen (usePointLayer.ts), for the selector. */
export type TPointTierStats = {
  /** Leaf chunks in the window, and how many have landed. */
  chunks: number;
  loaded: number;
  /** Observations decoded in the window, and drawn after decimation. */
  photons: number;
  drawn: number;
  stride: number;
  /** Milliseconds from the window's request to its first points, and to all. */
  firstPointsMs?: number;
  windowMs?: number;
  /** Milliseconds spent decoding, over the window. */
  decodeMs: number;
};

export const LAYER_KINDS = {
  COASTLINES: "coastlines",
  GRATICULES: "graticules",
  GRID: "grid",
  MASK: "mask",
  STREAMLINES: "streamlines",
  VOLUME: "volume",
  TEXTURE: "texture",
} as const;

export type TLayerKind = (typeof LAYER_KINDS)[keyof typeof LAYER_KINDS];

export const COASTLINE_RESOLUTIONS = {
  TEN_M: "10m",
  FIFTY_M: "50m",
} as const;

export type TCoastlineResolution =
  (typeof COASTLINE_RESOLUTIONS)[keyof typeof COASTLINE_RESOLUTIONS];

export const GRATICULE_SPACINGS = {
  FIFTEEN_DEGREES: 15,
  THIRTY_DEGREES: 30,
} as const;

export type TGraticuleSpacing =
  (typeof GRATICULE_SPACINGS)[keyof typeof GRATICULE_SPACINGS];

export const BUILTIN_LAYER_IDS = {
  COASTLINES: "coastlines",
  GRATICULES: "graticules",
  GRID: "grid",
  MASK: "mask",
  STREAMLINES: "streamlines",
  VOLUME: "volume",
} as const;

export const LAYER_OPACITY = {
  MIN: 0,
  MAX: 1,
  STEP: 0.05,
} as const;

export const STREAMLINE_LOADING_STAGES = {
  DATA: "Loading vector data",
  FIELD: "Preparing vector field",
  PATHS: "Computing streamlines",
} as const;

type TStreamlineLoadingStage =
  (typeof STREAMLINE_LOADING_STAGES)[keyof typeof STREAMLINE_LOADING_STAGES];

export type TLayerEntry = {
  id: string;
  kind: TLayerKind;
  name: string;
  visible: boolean;
  opacity: number;
  // land/sea cutout applied to texture layers
  maskMode: TLandSeaMaskMode;
};

export type TVolumeSelection = {
  variable: string;
  color: string;
  opacity: number;
};

function normalizeLayerOpacity(opacity: number) {
  if (!Number.isFinite(opacity)) {
    return LAYER_OPACITY.MAX;
  }
  if (opacity < LAYER_OPACITY.MIN) {
    return LAYER_OPACITY.MIN;
  }
  if (opacity > LAYER_OPACITY.MAX) {
    return LAYER_OPACITY.MAX;
  }
  return opacity;
}

export const BUILTIN_LAYER_NAMES = {
  [LAYER_KINDS.COASTLINES]: "Coastlines",
  [LAYER_KINDS.GRATICULES]: "Lat/Lon grid",
  [LAYER_KINDS.GRID]: "Data grid",
  [LAYER_KINDS.MASK]: "Land/sea mask",
  [LAYER_KINDS.STREAMLINES]: "Flow streamlines",
  [LAYER_KINDS.VOLUME]: "Volume",
} as const satisfies Record<
  Exclude<TLayerKind, typeof LAYER_KINDS.TEXTURE>,
  string
>;

type TBuiltinLayerKind = keyof typeof BUILTIN_LAYER_NAMES;
type TBuiltinLayerDefaults = Omit<TLayerEntry, "name" | "visible">;

const BUILTIN_LAYER_DEFAULTS: Record<TBuiltinLayerKind, TBuiltinLayerDefaults> =
  {
    [LAYER_KINDS.COASTLINES]: {
      id: BUILTIN_LAYER_IDS.COASTLINES,
      kind: LAYER_KINDS.COASTLINES,
      opacity: LAYER_OPACITY.MAX,
      maskMode: LAND_SEA_MASK_MODES.OFF,
    },
    [LAYER_KINDS.GRATICULES]: {
      id: BUILTIN_LAYER_IDS.GRATICULES,
      kind: LAYER_KINDS.GRATICULES,
      opacity: LAYER_OPACITY.MAX,
      maskMode: LAND_SEA_MASK_MODES.OFF,
    },
    [LAYER_KINDS.GRID]: {
      id: BUILTIN_LAYER_IDS.GRID,
      kind: LAYER_KINDS.GRID,
      opacity: LAYER_OPACITY.MAX,
      maskMode: LAND_SEA_MASK_MODES.OFF,
    },
    [LAYER_KINDS.MASK]: {
      id: BUILTIN_LAYER_IDS.MASK,
      kind: LAYER_KINDS.MASK,
      opacity: LAYER_OPACITY.MAX,
      maskMode: LAND_SEA_MASK_MODES.OFF,
    },
    [LAYER_KINDS.STREAMLINES]: {
      id: BUILTIN_LAYER_IDS.STREAMLINES,
      kind: LAYER_KINDS.STREAMLINES,
      opacity: 0.55,
      maskMode: LAND_SEA_MASK_MODES.OFF,
    },
    [LAYER_KINDS.VOLUME]: {
      id: BUILTIN_LAYER_IDS.VOLUME,
      kind: LAYER_KINDS.VOLUME,
      opacity: LAYER_OPACITY.MAX,
      maskMode: LAND_SEA_MASK_MODES.OFF,
    },
  };

const INITIAL_BUILTIN_LAYER_KINDS = [
  LAYER_KINDS.COASTLINES,
  LAYER_KINDS.GRID,
] as const satisfies readonly TBuiltinLayerKind[];

function createBuiltinLayer(kind: TBuiltinLayerKind): TLayerEntry {
  return {
    ...BUILTIN_LAYER_DEFAULTS[kind],
    name: BUILTIN_LAYER_NAMES[kind],
    visible: true,
  };
}

function initialLayerStack(): TLayerEntry[] {
  return INITIAL_BUILTIN_LAYER_KINDS.map(createBuiltinLayer);
}

export const useGlobeControlStore = defineStore("globeControl", {
  // eslint-disable-next-line max-lines-per-function
  state: () => {
    return {
      showCoastLines: true,
      showGraticules: false,
      showDistanceScale: true,
      coastlineResolution:
        COASTLINE_RESOLUTIONS.FIFTY_M as TCoastlineResolution,
      graticuleSpacing: GRATICULE_SPACINGS.THIRTY_DEGREES as TGraticuleSpacing,
      landSeaMaskChoice: LAND_SEA_MASK_MODES.OFF as TLandSeaMaskMode,
      // when true, use the textured versions; when false, use the simple versions
      landSeaMaskUseTexture: false,
      varnameSelector: "-", // the varname currently selected in the dropdown
      varnameDisplay: "-", // the varname currently shown on the globe (will be updated after loading)
      loading: false,
      varinfo: undefined as TVarInfo | undefined, // info about a dataset coming directly from the data
      selection: { low: 0, high: 0 } as TBounds, // all the knobs and buttons in GlobeControl which do not require a reload
      histogram: undefined as number[] | undefined, // selection-range histogram bins
      fullHistogram: undefined as number[] | undefined, // fixed histogram over full data range
      histogramSummary: undefined as THistogramSummary | undefined, // full-resolution (4096-bin) summary
      colormap: "viridis" as TColorMap,
      invertColormap: false,
      posterizeLevels: 0 as number,
      hideLowerBound: false,
      hideUpperBound: false,
      userBoundsLow: undefined as number | undefined,
      userBoundsHigh: undefined as number | undefined,
      dimSlidersValues: [] as (number | null)[],
      dimSlidersDisplay: [] as (number | null)[],
      isInitializingVariable: false,
      controlPanelVisible: true,
      datasetTitle: "" as string,
      projectionMode: PROJECTION_TYPES.NEARSIDE_PERSPECTIVE as TProjectionType,
      projectionCenter: { lat: 0, lon: 0 } as TProjectionCenter,
      isRotating: false,
      hoverEnabled: false,
      hoveredGridPoint: undefined as THoveredGridPoint | undefined,
      distanceScale: null as TDistanceScale | null,
      // ground metres one screen pixel covers below the camera, as rendered
      metersPerPixel: undefined as number | undefined,
      // lat/lon box of what the camera sees, refreshed while it moves
      viewFootprint: null as TViewFootprint | null,
      // the mounted grid loads only the part of a large level that is in
      // view, except the levels it found it has to load whole
      viewLoading: false,
      wholeLevels: [] as number[],
      catalogUrl: undefined as string | undefined,
      catalogData: undefined as TCatalog | undefined,
      // multi-resolution datasets: the level every consumer reads, and
      // whether the camera picks it (a manual pick turns that off)
      selectedLevel: 0 as number,
      levelAuto: true,
      // the point tier (levels.ts): the observations themselves are drawn
      // over the finest level, which `selectedLevel` then is
      pointTier: false,
      pointTierStats: undefined as TPointTierStats | undefined,
      // the colour range of the drawn points, in place of the level's data
      // range while the tier is on (a custom range still wins)
      pointTierBounds: undefined as TBounds | undefined,
      // what a plain left drag does on the tier's camera (pointPivot.ts)
      pointTierLeftDrag: POINT_PIVOT_LEFT_DRAGS.PAN as TPivotLeftDrag,
      // what the points are coloured by (pointTimes.ts), and whether the
      // source has times to colour by
      pointTierColour: POINT_COLOURS.HEIGHT as TPointColour,
      pointTierTimed: false,
      // the points' opacity, 0.05 to 1, and the noise cloud's under the
      // `both` stratum choice relative to it (pointBudget.ts)
      pointTierOpacity: 1,
      pointTierNoiseOpacity: NOISE_POINT_OPACITY,
      // the tilt the tier's camera is asked to animate to, degrees from
      // nadir (pointPivot.ts); the serial makes every request its own
      pointTierTilt: undefined as
        { degrees: number; serial: number } | undefined,
      // whether every level shares the coarsest level's colour range and
      // histogram (right for a mean, e.g. elevation) or each level takes its
      // own (right for a sum, e.g. a count, which grows 4x per level)
      levelRangeShared: true,
      // whether the next coarser level is drawn beneath a level loaded by
      // view, where its blocks have not landed or the budget left them out
      levelParentBeneath: true,
      // whether the window of the level in view was cut by its budget, so
      // that cells in view are missing (zoom in, or pick a coarser level)
      viewWindowClipped: false,
      // variables derived from a t-digest: the percentile shown, and the
      // two a percentile range is the difference of (0..100)
      digestPercentile: DEFAULT_PERCENTILE as number,
      digestPercentileLow: DEFAULT_PERCENTILE_LOW as number,
      digestPercentileHigh: DEFAULT_PERCENTILE_HIGH as number,
      // the stratum of the observations a digest variable reads (strata.ts):
      // the signal, the noise, or the two merged
      digestStratum: DEFAULT_STRATUM as TStratumChoice,
      // whether the blank cells of a digest variable are filled from their
      // neighbours, and the size of the Gaussian kernel, in cells across
      gapFill: false,
      gapFillSize: DEFAULT_GAP_FILL_SIZE as number,
      // the place whose distribution the panel keeps showing (a click), and
      // the cell it shows now: the pinned place at the level on screen, or
      // the cell under the cursor
      digestPin: undefined as { lat: number; lon: number } | undefined,
      digestProbe: undefined as TDigestProbe | undefined,
      // ── Live datasets ──────────────────────────────────────────────
      // A live dataset exposes only the currently-available timestep and is
      // followed automatically by polling the store's timestep endpoints.
      live: false, // whether the current dataset is a live dataset
      livePaused: false, // user paused auto-following the newest timestep
      liveConnected: false, // whether the long-poll is currently connected
      liveTimestep: undefined as number | undefined, // latest known live index
      // layer panel stack, ordered top → bottom; order determines render order
      layerStack: initialLayerStack(),
      // incremented to request a GeoTIFF image-layer export of the current grid
      gridExportRequest: 0 as number,
      gridExportLoading: false,
      streamlineAvailable: false,
      streamlineIncompatibility: undefined as string | undefined,
      streamlineLoading: false,
      streamlineProgress: undefined as number | undefined,
      streamlineLoadingStage:
        STREAMLINE_LOADING_STAGES.DATA as TStreamlineLoadingStage,
      streamlinePair: undefined as TVectorVariablePair | undefined,
      streamlineSelection: {
        automatic: true,
      } as TVectorVariableSelection,
      streamlineLevelInfo: undefined as TStreamlineLevelInfo | undefined,
      streamlineLevelIndex: 0,
      streamlineSelectionRevision: 0,
      streamlineMagnitudeRequested: false,
      streamlineMagnitudeDisplayed: false,
      streamlineMagnitudeInfo: undefined as TVectorMagnitudeInfo | undefined,
      streamlineMagnitudeDerivable: false,
      streamlineScalarRevision: 0,
      // will get incremented each time a new dataset OR a new variable in the
      // same dataset is loaded; used to trigger reactivity in child components
      // that need to reload data when the variable changes
      // if the value is even, the change is a new dataset; if odd, it's a
      // variable change within the same dataset
      newDatasetSignifier: 0 as number,
      volumeSelections: [] as TVolumeSelection[],
      volumeLoading: false,
      volumeProgress: undefined as number | undefined,
      volumeAvailable: false,
    };
  },
  actions: {
    signifyDatasetChange() {
      if (this.newDatasetSignifier % 2 === 0) {
        this.newDatasetSignifier += 2;
      } else {
        this.newDatasetSignifier += 1;
      }
      this.resetStreamlineSelection();
      this.pointTier = false;
      this.digestPin = undefined;
      this.digestProbe = undefined;
      this.volumeSelections = [];
      this.volumeLoading = false;
      this.volumeProgress = undefined;
    },
    signifyVariableChange() {
      if (this.newDatasetSignifier % 2 === 0) {
        this.newDatasetSignifier += 1;
      } else {
        this.newDatasetSignifier += 2;
      }
    },
    selectVariable(varname: string) {
      if (this.varnameSelector === varname) {
        return;
      }
      this.startLoading();
      this.varnameSelector = varname;
      this.signifyVariableChange();
    },
    selectLevel(index: number, manual = true) {
      if (manual) {
        this.levelAuto = false;
        this.pointTier = false;
      }
      if (this.selectedLevel !== index) {
        this.selectedLevel = index;
      }
    },
    // the points over the finest level, `index`; by hand unless told otherwise
    selectPointTier(index: number, manual = true) {
      this.selectLevel(index, manual);
      this.pointTier = true;
    },
    setPointTier(on: boolean) {
      this.pointTier = on;
    },
    setPointTierStats(stats: TPointTierStats | undefined) {
      this.pointTierStats = stats;
    },
    setPointTierBounds(bounds: TBounds | undefined) {
      this.pointTierBounds = bounds;
    },
    setPointTierLeftDrag(mode: TPivotLeftDrag) {
      this.pointTierLeftDrag = mode;
    },
    setPointTierColour(colour: TPointColour) {
      this.pointTierColour = colour;
    },
    setPointTierTimed(timed: boolean) {
      this.pointTierTimed = timed;
    },
    /**
     * The points on screen are coloured by time: chosen, and the source has
     * times. Their range is then labelled as dates.
     */
    pointTierColouredByTime(): boolean {
      return (
        this.pointTier &&
        this.pointTierColour === POINT_COLOURS.TIME &&
        this.pointTierTimed
      );
    },
    setPointTierOpacity(opacity: number) {
      this.pointTierOpacity = Number.isFinite(opacity)
        ? Math.min(
            Math.max(opacity, POINT_OPACITY_RANGE.min),
            POINT_OPACITY_RANGE.max
          )
        : 1;
    },
    requestPointTierTilt(degrees: number) {
      this.pointTierTilt = {
        degrees,
        serial: (this.pointTierTilt?.serial ?? 0) + 1,
      };
    },
    setLevelAuto(auto: boolean) {
      this.levelAuto = auto;
    },
    setLevelRangeShared(shared: boolean) {
      this.levelRangeShared = shared;
    },
    setLevelParentBeneath(shown: boolean) {
      this.levelParentBeneath = shown;
    },
    setViewWindowClipped(clipped: boolean) {
      this.viewWindowClipped = clipped;
    },
    setDigestPercentile(percentile: number) {
      if (Number.isFinite(percentile)) {
        this.digestPercentile = Math.min(Math.max(percentile, 0), 100);
      }
    },
    // the bounds of a percentile range, kept in order
    setDigestPercentileBounds(low: number, high: number) {
      if (!Number.isFinite(low) || !Number.isFinite(high)) {
        return;
      }
      const clamped = [low, high].map((v) => Math.min(Math.max(v, 0), 100));
      this.digestPercentileLow = Math.min(...clamped);
      this.digestPercentileHigh = Math.max(...clamped);
    },
    setDigestStratum(choice: unknown) {
      if (isStratumChoice(choice)) {
        this.digestStratum = choice;
      }
    },
    setGapFill(enabled: boolean) {
      this.gapFill = enabled;
    },
    setGapFillSize(size: number) {
      if ((GAP_FILL_SIZES as readonly number[]).includes(size)) {
        this.gapFillSize = size;
      }
    },
    setDigestPin(pin: { lat: number; lon: number } | undefined) {
      this.digestPin = pin;
      if (!pin) {
        this.digestProbe = undefined;
      }
    },
    setDigestProbe(probe: TDigestProbe | undefined) {
      // typed arrays: never walked by the reactivity system
      this.digestProbe = probe && markRaw(probe);
    },
    isNewDataset(): boolean {
      return this.newDatasetSignifier % 2 === 0;
    },
    isVariableChange(): boolean {
      return this.newDatasetSignifier % 2 === 1;
    },
    toggleRotating() {
      this.isRotating = !this.isRotating;
    },
    setLive(live: boolean) {
      this.live = live;
      if (!live) {
        this.livePaused = false;
        this.liveConnected = false;
        this.liveTimestep = undefined;
      }
    },
    toggleLivePaused() {
      this.livePaused = !this.livePaused;
    },
    setLiveConnected(connected: boolean) {
      this.liveConnected = connected;
    },
    setLiveTimestep(timestep: number) {
      this.liveTimestep = timestep;
    },
    toggleHoverEnabled() {
      this.hoverEnabled = !this.hoverEnabled;
      if (!this.hoverEnabled) {
        this.clearHoveredGridPoint();
      }
    },
    toggleCoastLines() {
      this.showCoastLines = !this.showCoastLines;
    },
    toggleGraticules() {
      this.showGraticules = !this.showGraticules;
    },
    startLoading() {
      this.loading = true;
      this.hoveredGridPoint = undefined;
    },
    stopLoading(updateDisplay = true) {
      this.loading = false;
      if (!updateDisplay) {
        return;
      }
      this.varnameDisplay = this.varnameSelector;
      for (let i = 0; i < this.dimSlidersValues.length; i++) {
        this.dimSlidersDisplay[i] = this.dimSlidersValues[i];
      }
    },
    updateVarInfo(varinfo: TVarInfo, indices: number[]) {
      const sliderValuesChanged =
        indices.length !== this.dimSlidersValues.length ||
        indices.some((index, i) => index !== this.dimSlidersValues[i]);
      if (sliderValuesChanged) {
        this.isInitializingVariable = true;
        this.dimSlidersValues = indices;
        this.dimSlidersDisplay = indices;
      }

      this.varinfo = varinfo;
    },
    updateLowUserBound(low: number | string | undefined) {
      if (typeof low === "string") {
        if (low.trim() === "") {
          low = undefined;
        } else {
          low = parseFloat(low);
        }
      }
      this.userBoundsLow = low;
    },
    updateHighUserBound(high: number | string | undefined) {
      if (typeof high === "string") {
        if (high.trim() === "") {
          high = undefined;
        } else {
          high = parseFloat(high);
        }
      }
      this.userBoundsHigh = high;
    },
    resetUserBounds() {
      this.userBoundsLow = undefined;
      this.userBoundsHigh = undefined;
    },
    updateBounds(bounds: TBounds) {
      this.selection = bounds;
    },
    updateHistogram(histogram: number[] | undefined) {
      this.histogram = histogram;
    },
    updateFullHistogram(histogram: number[] | undefined) {
      this.fullHistogram = histogram;
    },
    updateHistogramSummary(summary: THistogramSummary | undefined) {
      this.histogramSummary = summary;
    },
    setControlPanelVisible(visible: boolean) {
      this.controlPanelVisible = visible;
    },
    addTextureLayer(id: string, name: string, visible = true) {
      // insert at the top of the stack
      this.layerStack.unshift({
        id,
        kind: LAYER_KINDS.TEXTURE,
        name,
        visible,
        opacity: LAYER_OPACITY.MAX,
        maskMode: LAND_SEA_MASK_MODES.OFF,
      });
    },
    removeLayer(id: string) {
      this.layerStack = this.layerStack.filter((layer) => layer.id !== id);
    },
    restoreBuiltinLayer(kind: TLayerKind) {
      if (
        kind === LAYER_KINDS.TEXTURE ||
        this.layerStack.some((layer) => layer.kind === kind)
      ) {
        return;
      }
      this.layerStack.unshift(createBuiltinLayer(kind));
    },
    updateTextureLayer(
      id: string,
      patch: Partial<Pick<TLayerEntry, "visible" | "maskMode">>
    ) {
      const layer = this.layerStack.find((entry) => entry.id === id);
      if (layer) {
        Object.assign(layer, patch);
      }
    },
    updateLayerOpacity(id: string, opacity: number) {
      const layer = this.layerStack.find((entry) => entry.id === id);
      if (layer) {
        layer.opacity = normalizeLayerOpacity(opacity);
      }
    },
    toggleLayerVisibility(id: string) {
      const layer = this.layerStack.find((entry) => entry.id === id);
      if (layer) {
        if (id === BUILTIN_LAYER_IDS.STREAMLINES) {
          this.setStreamlineLayerEnabled(!layer.visible);
        } else {
          layer.visible = !layer.visible;
        }
      }
    },
    isStreamlineLayerEnabled() {
      return Boolean(
        this.layerStack.find(
          (entry) => entry.id === BUILTIN_LAYER_IDS.STREAMLINES
        )?.visible
      );
    },
    setStreamlineLayerEnabled(enabled: boolean) {
      if (enabled) {
        this.restoreBuiltinLayer(LAYER_KINDS.STREAMLINES);
      }
      const layer = this.layerStack.find(
        (entry) => entry.id === BUILTIN_LAYER_IDS.STREAMLINES
      );
      if (layer) {
        layer.visible = enabled;
        if (!enabled) {
          this.setStreamlineMagnitudeDisplayed(false);
        }
      }
    },
    // Whether a large level is loaded as the part in view rather than whole.
    // Streamlines and volumes read the whole level.
    loadsLevelByView(level: number) {
      return (
        this.viewLoading &&
        !this.wholeLevels.includes(level) &&
        !this.isStreamlineLayerEnabled() &&
        !this.isVolumeLayerEnabled()
      );
    },
    isVolumeLayerEnabled() {
      return Boolean(
        this.layerStack.find((entry) => entry.id === BUILTIN_LAYER_IDS.VOLUME)
          ?.visible
      );
    },
    setVolumeLayerEnabled(enabled: boolean) {
      if (enabled) {
        this.restoreBuiltinLayer(LAYER_KINDS.VOLUME);
      }
      const layer = this.layerStack.find(
        (entry) => entry.id === BUILTIN_LAYER_IDS.VOLUME
      );
      if (layer) {
        layer.visible = enabled;
      }
    },
    setVolumeSelections(selections: TVolumeSelection[]) {
      this.volumeSelections = selections.slice(0, 4).map((selection) => ({
        variable: selection.variable,
        color: selection.color,
        opacity: normalizeLayerOpacity(selection.opacity),
      }));
    },
    // moves the entry so it ends up at index `toIndex` of the resulting array
    moveLayer(id: string, toIndex: number) {
      const fromIndex = this.layerStack.findIndex((entry) => entry.id === id);
      if (fromIndex === -1) {
        return;
      }
      const [entry] = this.layerStack.splice(fromIndex, 1);
      const clamped = Math.max(0, Math.min(this.layerStack.length, toIndex));
      this.layerStack.splice(clamped, 0, entry);
    },
    requestGridExport() {
      this.gridExportRequest++;
    },
    setStreamlinePair(pair?: TVectorVariablePair) {
      this.streamlinePair = pair;
      this.streamlineAvailable = pair !== undefined;
      this.streamlineIncompatibility = undefined;
    },
    setStreamlineSelection(selection: TVectorVariableSelection) {
      const previous = this.streamlineSelection;
      if (
        previous.automatic === selection.automatic &&
        previous.u === selection.u &&
        previous.v === selection.v
      ) {
        return;
      }
      this.streamlineSelection = selection;
      this.streamlineIncompatibility = undefined;
      this.streamlineLevelInfo = undefined;
      this.streamlineLevelIndex = 0;
      this.streamlineSelectionRevision++;
    },
    setStreamlineLevelInfo(info?: TStreamlineLevelInfo) {
      const previous = this.streamlineLevelInfo;
      const sameLevelAxis = Boolean(
        previous && info && levelAxesAreIdentical(previous, info)
      );
      if (sameLevelAxis || (!previous && !info)) {
        return;
      }
      this.streamlineLevelInfo = info;
      this.streamlineLevelIndex = 0;
    },
    setStreamlineLevelIndex(index: number, refresh = true) {
      const maximum = Math.max(
        0,
        (this.streamlineLevelInfo?.values.length ?? 1) - 1
      );
      const nextIndex = Math.min(maximum, Math.max(0, Math.trunc(index)));
      if (this.streamlineLevelIndex === nextIndex) {
        return;
      }
      this.streamlineLevelIndex = nextIndex;
      if (refresh) {
        this.streamlineSelectionRevision++;
      }
    },
    setStreamlineMagnitudeDisplayed(displayed: boolean, refresh = false) {
      this.streamlineMagnitudeRequested = displayed;
      if (this.streamlineMagnitudeDisplayed === displayed) {
        return;
      }
      this.streamlineMagnitudeDisplayed = displayed;
      if (refresh) {
        this.streamlineScalarRevision++;
      }
    },
    setStreamlineMagnitudeInfo(info?: TVectorMagnitudeInfo, derivable = false) {
      this.streamlineMagnitudeInfo = info
        ? {
            standardName: info.standardName,
            longName: info.longName,
            units: info.units,
          }
        : undefined;
      this.streamlineMagnitudeDerivable = Boolean(info && derivable);
      const shouldDisplay =
        this.streamlineMagnitudeRequested && this.streamlineMagnitudeDerivable;
      if (this.streamlineMagnitudeDisplayed === shouldDisplay) {
        return;
      }
      this.streamlineMagnitudeDisplayed = shouldDisplay;
      if (!shouldDisplay && this.varnameDisplay !== this.varnameSelector) {
        this.streamlineScalarRevision++;
      }
    },
    resetStreamlineSelection() {
      this.streamlinePair = undefined;
      this.streamlineAvailable = false;
      this.streamlineIncompatibility = undefined;
      this.streamlineLevelInfo = undefined;
      this.streamlineLevelIndex = 0;
      this.setStreamlineSelection({ automatic: true });
    },
    setHoveredGridPoint(point: THoveredGridPoint) {
      this.hoveredGridPoint = point;
    },
    clearHoveredGridPoint() {
      this.hoveredGridPoint = undefined;
    },
    resetExcept(keysToKeep: (keyof typeof this.$state)[] = []) {
      const state = this as Record<keyof typeof this.$state, unknown>;
      const saved = Object.fromEntries(
        keysToKeep.map((k) => {
          const val = state[k];
          return [
            k,
            val !== null && typeof val === "object"
              ? JSON.parse(JSON.stringify(val))
              : val,
          ];
        })
      );
      this.$reset();
      this.$patch(saved);
    },
  },
});

export type TGlobeControlStoreKeys = keyof ReturnType<
  typeof useGlobeControlStore
>["$state"];
